import { useState } from 'react';

import { getNumber } from '../../utilities';
import { useConfirm } from '../../molecules';
import { ColumnsBuilder } from '../../molecules';
import { Button, SubmitButton } from '../../components';
import { Modal, Form, FormItem } from '../../components';
import { Input, Dropdown, Toggle } from '../../components';
import { convertItemToForm, convertFormToPayload } from './utilities';
import type { ItemType, FormType, ApiPayload } from './useCampaignActions';

type CampaignModalProps = {
  loading: boolean;
  initialValues?: ItemType;
  agentOptions: OptionType[];
  phoneOptions: OptionType[];

  onClose: () => void;
  onSumbit: (payload: ApiPayload) => void;
};

const campaignCSVImportDefaultStateOptions: OptionType<CampaignCSVImportDefaultState>[] =
  [
    { value: 'AI_READY_TO_START', label: 'AI_READY_TO_START' },
    { value: 'DIALER_READY_TO_START', label: 'DIALER_READY_TO_START' },
    { value: 'NO_STATE', label: 'NO_STATE' },
  ];

const dialerFailedFallbackStateOptions: OptionType<DialerFailedFallbackState>[] =
  [
    { value: 'AI_READY_TO_START', label: 'AI_READY_TO_START' },
    { value: 'AI_FAILED', label: 'AI_FAILED' },
    { value: 'NO_STATE', label: 'NO_STATE' },
  ];

const defaultForm: Partial<ItemType> = {
  status: 'active',
  langfuse_enabled: true,
  dialer_attempts_per_day: 3,
  dialer_total_attempts_limit: 100,
  csv_import_default_state: 'NO_STATE',
};

const needShowConfirm = (initial?: ItemType, current?: FormType) => {
  return initial?.status === 'active' && current?.status === 'inactive';
};

const confirmMessage =
  'Setting this campaign to inactive will delete pending tasks and ' +
  'reset in-flight contacts to a paused state. In-flight calls ' +
  'will finish naturally. Continue?';

const CampaignModal: React.FC<CampaignModalProps> = ({
  agentOptions,
  phoneOptions,
  loading,
  initialValues,
  onClose,
  onSumbit,
}) => {
  const [values, setValues] = useState<FormType>(() => {
    return convertItemToForm(initialValues || defaultForm);
  });
  const { element: confirmElement, open } = useConfirm();

  const handleSubmit = () => {
    const onConfirm = () => {
      return onSumbit?.(convertFormToPayload(values, initialValues));
    };

    if (!values) return;
    if (!needShowConfirm(initialValues, values)) return onConfirm();

    return open({ text: confirmMessage, onConfirm: onConfirm });
  };

  return (
    <Form<FormType> initialValue={values} onChange={setValues}>
      <Modal
        open
        size="big"
        title={initialValues ? 'Edit Campaign' : 'Add Campaign'}
        onClose={onClose}
        bottomActions={
          <>
            <SubmitButton
              type="primary"
              loading={loading}
              onClick={handleSubmit}
            >
              Save
            </SubmitButton>
            <Button onClick={onClose}>Cancel</Button>
          </>
        }
      >
        <div className="flex flex-col gap-y-md">
          <FormItem<FormType> label="Name" name="name" required>
            <Input />
          </FormItem>
          <FormItem<FormType> label="Description" name="description" required>
            <Input />
          </FormItem>
          <FormItem<FormType> label="Status" name="status" required>
            <Dropdown
              options={[
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Inactive' },
                { value: 'completed', label: 'Completed' },
              ]}
            />
          </FormItem>

          <FormItem<FormType>
            required
            label="Outbound Phone Id"
            name="outbound_phone_id"
          >
            <Dropdown options={phoneOptions} />
          </FormItem>
          <FormItem<FormType>
            required
            name="default_agent_id"
            label="Default Agent Id"
          >
            <Dropdown options={agentOptions} />
          </FormItem>

          <FormItem<FormType>
            name="time_slot_duration_minutes"
            label="Time Slot Duration (minutes)"
          >
            <Input numeric placeholder="e.g. 10" />
          </FormItem>

          <FormItem<FormType>
            name="max_concurrent_calls"
            label="Max Concurrent Calls"
          >
            <Input numeric placeholder="e.g. 10" />
          </FormItem>

          <FormItem<FormType>
            name="max_slot_capacity"
            label="Max Slot Capacity"
          >
            <Input numeric placeholder="e.g. 10" />
          </FormItem>

          <FormItem<FormType>
            required
            name="csv_import_default_state"
            label="CSV Import Default State"
          >
            <Dropdown options={campaignCSVImportDefaultStateOptions} />
          </FormItem>

          <FormItem<FormType>
            required
            name="dialer_failed_fallback_state"
            label="Dialer Failed Fallback State"
          >
            <Dropdown options={dialerFailedFallbackStateOptions} />
          </FormItem>

          <FormItem<FormType>
            required
            name="dialer_attempts_per_day"
            label="Dialer MAX Attempts Per Day"
          >
            <Input numeric placeholder="e.g. 3" />
          </FormItem>
          <FormItem<FormType>
            required
            name="dialer_total_attempts_limit"
            label="Dialer Total Attempts Limit"
          >
            <Input numeric placeholder="e.g. 100" />
          </FormItem>
          <FormItem<FormType>
            label="Retry Backoff (minutes)"
            name="dialer_retry_backoff_minutes"
            tooltip="ints separated by comas, e.g. 10,30,120,480"
          >
            <Input placeholder="ints, e.g. 10,30,120,480" />
          </FormItem>

          <FormItem<FormType>
            label="Langfuse enabled"
            name="langfuse_enabled"
            tooltip="When off, no LLM traces are emitted for this campaign."
          >
            <Toggle />
          </FormItem>
          <FormItem<FormType>
            name="langfuse_sampling_rate"
            label="Langfuse Sampling Rate"
            tooltip="Percentage of trace roots recorded."
            validate={value => {
              const numeric = getNumber(value);
              if (typeof numeric !== 'number') return null;

              if (numeric < 0 || numeric > 100) {
                return 'Value must be in [0, 100] range';
              }

              return null;
            }}
          >
            <Input numeric />
          </FormItem>
          <FormItem<FormType>
            inline={false}
            label="Calling Schedule (JSON)"
            name="dialer_calling_schedule"
          >
            <Input multiline rows={5} />
          </FormItem>

          <FormItem<FormType>
            inline={false}
            name="export_fields"
            tooltip="Comma-separated field paths. Prefixes: data.* (contact JSON),
              metadata.* (latest state details)"
            label="Export Fields (CSV columns)"
          >
            <Input
              multiline
              placeholder="phone,email,company,data.some_key,metadata.user_details"
            />
          </FormItem>

          <FormItem<FormType>
            inline={false}
            name="monitoring_fields"
            label="Monitoring Fields"
          >
            <ColumnsBuilder />
          </FormItem>
        </div>
      </Modal>
      {confirmElement}
    </Form>
  );
};

export default CampaignModal;
